import { useState, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getPublicGenieById } from "@/data/discovery/genie-catalog";
import { AgentProvisionService } from "@/services/agent/core";

interface QuickCreateItem {
  personaId: string;
  customName?: string;
}

interface CreationResult {
  personaId: string;
  personaName: string;
  success: boolean;
  error?: string;
}

interface QuickCreateProgress {
  current: number;
  total: number;
  results: CreationResult[];
}

interface UseQuickCreateGeniesOptions {
  onComplete?: () => void;
}

export const useQuickCreateGenies = (options?: UseQuickCreateGeniesOptions) => {
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<QuickCreateProgress>({
    current: 0,
    total: 0,
    results: [],
  });

  const resetProgress = useCallback(() => {
    setProgress({ current: 0, total: 0, results: [] });
  }, []);

  const mutation = useMutation({
    mutationFn: async ({
      items,
      targetUserId,
      businessName,
    }: {
      items: QuickCreateItem[];
      targetUserId?: string;
      businessName?: string;
    }) => {
      const results: CreationResult[] = [];
      setProgress({ current: 0, total: items.length, results: [] });

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const persona = getPublicGenieById(item.personaId);

        if (!persona) {
          const result: CreationResult = {
            personaId: item.personaId,
            personaName: item.personaId,
            success: false,
            error: "Persona not found",
          };
          results.push(result);
          setProgress({ current: i + 1, total: items.length, results: [...results] });
          continue;
        }

        try {
          await AgentProvisionService.createFromPersona(item.personaId, {
            businessName,
            customName: item.customName,
            impersonatedUserId: targetUserId,
          });

          results.push({
            personaId: item.personaId,
            personaName: persona.name,
            success: true,
          });
        } catch (error) {
          results.push({
            personaId: item.personaId,
            personaName: persona.name,
            success: false,
            error: error instanceof Error ? error.message : "Creation failed",
          });
        }

        setProgress({ current: i + 1, total: items.length, results: [...results] });
      }

      return results;
    },
    onSuccess: (results) => {
      const succeeded = results.filter((r) => r.success).length;
      const failed = results.filter((r) => !r.success).length;

      if (failed === 0) {
        toast.success(`${succeeded} genie${succeeded !== 1 ? "s" : ""} created successfully`);
      } else if (succeeded > 0) {
        toast.warning(`${succeeded} created, ${failed} failed`);
      } else {
        toast.error("All genie creations failed");
      }

      queryClient.invalidateQueries({ queryKey: ["agents"] });
      options?.onComplete?.();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Quick create failed");
    },
  });

  return {
    createGenies: mutation.mutateAsync,
    isCreating: mutation.isPending,
    progress,
    resetProgress,
  };
};
